import { useEffect, useState, useCallback } from "react";
import Layout from "../components/Layout";
import { http, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import SearchBar, { matchesSearch } from "../components/SearchBar";
import AgreementDialog from "../components/AgreementDialog";
import ProjectsExportDialog from "../components/ProjectsExportDialog";
import {
  Buildings, Plus, PencilSimple, Trash, User, Phone, MapPin, CurrencyInr,
  ChartLine, ListChecks, Calendar, ArrowCounterClockwise, UserCircleGear,
  Wallet, PaperPlaneTilt, EnvelopeSimple, Vault, FilePdf, FileText,
} from "@phosphor-icons/react";

const STATUS_META = {
  active: { label: "Active", color: "bg-blue-100 text-blue-800", border: "border-blue-600" },
  on_hold: { label: "On Hold", color: "bg-amber-100 text-amber-800", border: "border-amber-600" },
  completed: { label: "Completed", color: "bg-emerald-100 text-emerald-800", border: "border-emerald-600" },
  cancelled: { label: "Cancelled", color: "bg-zinc-200 text-zinc-800", border: "border-zinc-400" },
};

const emptyForm = {
  name: "", title: "", client_name: "", client_contact: "", client_address: "",
  scope: "", category: "civil", fee_type: "lumpsum",
  lumpsum_amount: "", rate_per_sqft: "", area_sqft: "",
  start_date: "", end_date: "", status: "active", manager_id: "__none__",
};

function money(n) {
  if (n === null || n === undefined || n === "") return "—";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })
    .format(Number(n) || 0);
}

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [managers, setManagers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [advancesFor, setAdvancesFor] = useState(null);
  const [loginEmailFor, setLoginEmailFor] = useState(null);
  const [agreementFor, setAgreementFor] = useState(null);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");   // 'all' | 'civil' | 'interior'
  const [exportOpen, setExportOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [pRes, uRes] = await Promise.all([
        http.get("/projects"),
        http.get("/users").catch(() => ({ data: [] })),
      ]);
      setProjects(pRes.data);
      setManagers(uRes.data.filter((u) => u.role === "manager" || u.role === "admin"));
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filteredProjects = projects
    .filter((p) => catFilter === "all" ? true : (p.category || "civil") === catFilter)
    .filter((p) => matchesSearch(search, p.name, p.client_name));

  const civilCount = projects.filter((p) => (p.category || "civil") === "civil").length;
  const interiorCount = projects.filter((p) => p.category === "interior").length;

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await http.delete(`/projects/${encodeURIComponent(pendingDelete.name)}`);
      toast.success(`Deleted project ${pendingDelete.name}`);
      setPendingDelete(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDeleting(false); }
  };

  return (
    <Layout
      title="Projects & Jobs"
      subtitle="Create a Job before assigning any tasks. All client details, scope, and fees stay attached to the project."
      actions={
        <div className="flex items-center gap-2">
          <Button
            data-testid="export-projects-pdf-button"
            onClick={() => setExportOpen(true)}
            variant="outline"
            className="rounded-none h-11 border-2 border-amber-700 text-amber-800 hover:bg-amber-50 font-mono uppercase tracking-widest text-xs px-4"
          >
            <FilePdf size={14} weight="bold" className="mr-2" /> Export PDF
          </Button>
          <Button
            data-testid="new-project-button"
            onClick={() => { setEditing(null); setCreating(true); }}
            className="rounded-none h-11 bg-orange-600 hover:bg-orange-700 text-white font-mono uppercase tracking-widest text-xs px-5 shadow-brutal-sm"
          >
            <Plus size={14} weight="bold" className="mr-2" /> New Project
          </Button>
        </div>
      }
    >
      {projects.length === 0 && (
        <div className="border border-dashed border-zinc-300 p-16 text-center bg-white" data-testid="projects-empty">
          <Buildings size={40} weight="bold" className="mx-auto text-zinc-300" />
          <div className="label-overline text-zinc-500 mt-3">No projects yet</div>
          <p className="text-sm text-zinc-500 mt-2 max-w-md mx-auto">
            Create your first Job — Job No., client details, scope, and fees. Tasks can only be assigned to an existing Project.
          </p>
        </div>
      )}

      {projects.length > 0 && (
        <div className="mb-5 flex flex-col sm:flex-row items-stretch sm:items-center gap-3" data-testid="projects-search">
          {/* Category segmented slider */}
          <div className="inline-flex rounded-none border-2 border-zinc-900 overflow-hidden shrink-0"
               data-testid="projects-category-filter" role="tablist" aria-label="Project category filter">
            {[
              { k: "all",      label: "All",          count: projects.length },
              { k: "civil",    label: "Civil Work",   count: civilCount },
              { k: "interior", label: "Interior",     count: interiorCount },
            ].map((opt) => {
              const active = catFilter === opt.k;
              return (
                <button
                  key={opt.k}
                  role="tab"
                  aria-selected={active}
                  onClick={() => setCatFilter(opt.k)}
                  data-testid={`projects-cat-${opt.k}`}
                  className={
                    "font-mono uppercase tracking-widest text-[10px] px-3 py-2 border-r-2 border-zinc-900 last:border-r-0 transition-colors " +
                    (active
                      ? "bg-zinc-900 text-white"
                      : "bg-white text-zinc-900 hover:bg-zinc-100")
                  }
                >
                  {opt.label} <span className={"ml-1 " + (active ? "text-amber-300" : "text-zinc-400")}>({opt.count})</span>
                </button>
              );
            })}
          </div>
          <SearchBar
            value={search}
            onChange={setSearch}
            testId="projects-search-input"
            placeholder="Search by Job No. or Client Name…"
          />
          <span className="label-overline text-zinc-500 whitespace-nowrap shrink-0">
            {filteredProjects.length} of {projects.length}
          </span>
        </div>
      )}

      {projects.length > 0 && filteredProjects.length === 0 && (
        <div className="border border-dashed border-zinc-300 p-10 text-center bg-white" data-testid="projects-search-empty">
          <div className="label-overline text-zinc-500">No projects match &quot;{search}&quot;</div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filteredProjects.map((p) => {
          const meta = STATUS_META[p.status] || STATUS_META.active;
          const fee = p.fee_summary || {};
          return (
            <div key={p.name} data-testid={`project-row-${p.name}`}
                 className={`bg-white border-t-2 ${meta.border} border-x border-b border-zinc-300`}>
              <div className="p-4 border-b border-zinc-200 flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="label-overline text-zinc-500">Job No.</div>
                  <div className="font-display font-black text-xl tracking-tighter text-zinc-900 leading-tight">{p.name}</div>
                  {p.title && <div className="text-sm text-zinc-700 mt-0.5 truncate">{p.title}</div>}
                </div>
                <span className={`label-overline px-2 py-1 ${meta.color}`}>{meta.label}</span>
                <span
                  data-testid={`project-cat-badge-${p.name}`}
                  className={`label-overline px-2 py-1 border ${
                    (p.category || "civil") === "interior"
                      ? "border-fuchsia-500 text-fuchsia-700 bg-fuchsia-50"
                      : "border-emerald-500 text-emerald-700 bg-emerald-50"
                  }`}
                  title="Project category"
                >
                  {(p.category || "civil") === "interior" ? "Interior" : "Civil"}
                </span>
              </div>

              <div className="p-4 space-y-2 text-sm">
                <Row icon={User} label="Client" value={p.client_name || "—"} />
                <Row icon={Phone} label="Contact" value={p.client_contact || "—"} />
                <Row icon={MapPin} label="Address" value={p.client_address || "—"} multiline />
                {p.scope && <Row icon={ListChecks} label="Scope" value={p.scope} multiline />}
                <Row
                  icon={UserCircleGear} label="Project Manager"
                  value={p.manager_name
                    ? `${p.manager_name}${p.manager_email ? ` · ${p.manager_email}` : ""}`
                    : <span className="text-zinc-400 italic">Not assigned</span>}
                />
                {p.fee_type === "lumpsum" && (
                  <Row icon={CurrencyInr} label="Fee (Lumpsum)" value={money(p.total_fee)} />
                )}
                {p.fee_type === "sqft" && (
                  <Row icon={CurrencyInr} label="Fee"
                       value={`${money(p.rate_per_sqft)}/sqft × ${p.area_sqft || 0} sqft = ${money(p.total_fee)}`} />
                )}
                {(p.start_date || p.end_date) && (
                  <Row icon={Calendar} label="Timeline"
                       value={`${p.start_date || "—"} → ${p.end_date || "—"}`} />
                )}

                {/* Fee Collection Summary */}
                <div className="mt-3 border border-emerald-400 bg-emerald-50/60 p-3" data-testid={`fee-summary-${p.name}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="label-overline text-emerald-800 flex items-center gap-1.5">
                      <Wallet size={12} weight="bold" /> Fee Collection Summary
                    </div>
                    <button
                      data-testid={`manage-advances-${p.name}`}
                      onClick={() => setAdvancesFor(p)}
                      className="label-overline text-emerald-800 hover:text-emerald-900 underline underline-offset-2 flex items-center gap-1"
                    >
                      <Vault size={11} weight="bold" /> Manage Advances
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-y-1.5 gap-x-3 text-xs font-mono">
                    <SummaryLine k="Total Fee" v={money(fee.total_fee)} />
                    <SummaryLine k="Advance Received" v={money(fee.advance_total)}
                                 sub={`${fee.advance_count || 0} entr${(fee.advance_count || 0) === 1 ? "y" : "ies"}`} />
                    <SummaryLine k="Invoices Billed" v={money(fee.invoiced)} />
                    <SummaryLine k="Invoices Paid" v={money(fee.invoice_paid)} accent="text-emerald-700" />
                    <SummaryLine k="Total Collected" v={money(fee.collected)} accent="text-emerald-800" bold />
                    <SummaryLine k="Outstanding" v={money(fee.outstanding)}
                                 accent={fee.outstanding > 0 ? "text-orange-700" : "text-zinc-500"} bold />
                  </div>
                </div>

                {(p.client_login?.username || p.client_login?.initial_password) && (
                  <div className="mt-3 border border-purple-400 bg-purple-50/60 p-3" data-testid={`client-login-${p.name}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="label-overline text-purple-700">Client Portal Login</div>
                      <button
                        data-testid={`send-login-email-${p.name}`}
                        onClick={() => setLoginEmailFor(p)}
                        className="label-overline text-purple-800 hover:text-purple-900 underline underline-offset-2 flex items-center gap-1"
                      >
                        <PaperPlaneTilt size={11} weight="bold" /> Send Login Details
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs font-mono text-zinc-800">
                      <div>
                        <div className="label-overline text-zinc-500">Username</div>
                        <div className="mt-0.5 break-all" data-testid={`client-username-${p.name}`}>{p.client_login.username || "—"}</div>
                      </div>
                      <div>
                        <div className="label-overline text-zinc-500">Password</div>
                        <div className="mt-0.5 break-all" data-testid={`client-password-${p.name}`}>{p.client_login.initial_password || "—"}</div>
                      </div>
                    </div>
                    <div className="text-[10px] text-zinc-500 mt-2 leading-relaxed">
                      Share these credentials with your client. If the client forgets, click &quot;Reset Password&quot; below to restore the default.
                    </div>
                  </div>
                )}
              </div>

              <div className="px-4 py-3 border-t border-zinc-200 bg-zinc-50 flex items-center justify-between text-xs">
                <div className="flex items-center gap-3 font-mono text-zinc-600">
                  <span className="flex items-center gap-1">
                    <ChartLine size={11} weight="bold" /> {p.progress_pct}% · {p.complete_tasks}/{p.total_tasks} tasks
                  </span>
                  {p.total_value > 0 && (
                    <span className="text-orange-700">
                      {money(p.billed_amount)} / {money(p.total_value)} billed
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    data-testid={`agreement-${p.name}`}
                    onClick={() => setAgreementFor(p)}
                    title="Create / edit architectural agreement"
                    className="label-overline text-orange-700 hover:text-orange-900 px-2 py-1 flex items-center gap-1"
                  >
                    <FileText size={11} weight="bold" /> Agreement
                  </button>
                  <ExportPdfButton project={p} />
                  {p.client_login?.username && (
                    <ResetPasswordButton project={p} onDone={load} />
                  )}
                  <button
                    data-testid={`edit-project-${p.name}`}
                    onClick={() => { setEditing(p); setCreating(true); }}
                    className="label-overline text-zinc-600 hover:text-blue-700 px-2 py-1 flex items-center gap-1"
                  >
                    <PencilSimple size={11} weight="bold" /> Edit
                  </button>
                  <button
                    data-testid={`delete-project-${p.name}`}
                    onClick={() => setPendingDelete(p)}
                    className="label-overline text-zinc-500 hover:text-red-600 px-2 py-1 flex items-center gap-1"
                  >
                    <Trash size={11} weight="bold" /> Delete
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <ProjectDialog
        open={creating}
        onOpenChange={setCreating}
        editing={editing}
        managers={managers}
        onSaved={() => { setCreating(false); setEditing(null); load(); }}
      />

      <AdvancesDialog
        project={advancesFor}
        onOpenChange={(v) => !v && setAdvancesFor(null)}
        onChanged={load}
      />

      <SendLoginDialog
        project={loginEmailFor}
        onOpenChange={(v) => !v && setLoginEmailFor(null)}
      />

      <AgreementDialog
        project={agreementFor}
        open={!!agreementFor}
        onOpenChange={(v) => !v && setAgreementFor(null)}
      />

      <AlertDialog open={!!pendingDelete} onOpenChange={(v) => !v && setPendingDelete(null)}>
        <AlertDialogContent className="rounded-none border-2 border-zinc-900 shadow-brutal">
          <AlertDialogHeader>
            <div className="label-overline text-red-600 mb-1">Destructive Action</div>
            <AlertDialogTitle className="font-display text-2xl font-black tracking-tighter uppercase">
              Delete project {pendingDelete?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-600">
              This permanently deletes the project, <strong>all its tasks, deliverables, client requests, advance records, and invoice history</strong>. The linked client account will be deleted. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-project"
              className="rounded-none border-zinc-400 font-mono uppercase tracking-widest text-xs hover:bg-zinc-100">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-project" disabled={deleting} onClick={confirmDelete}
              className="rounded-none bg-red-600 hover:bg-red-700 text-white font-mono uppercase tracking-widest text-xs">
              {deleting ? "Deleting…" : "Yes, Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ProjectsExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        projects={projects}
        initialCategory={catFilter}
      />
    </Layout>
  );
}

function ResetPasswordButton({ project, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const doReset = async () => {
    setBusy(true);
    try {
      await http.post(`/projects/${encodeURIComponent(project.name)}/reset-client-password`);
      toast.success("Password reset");
      setOpen(false);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <>
      <button
        data-testid={`reset-password-${project.name}`}
        onClick={() => setOpen(true)}
        className="label-overline text-zinc-600 hover:text-purple-700 px-2 py-1 flex items-center gap-1"
      >
        <ArrowCounterClockwise size={11} weight="bold" /> Reset Pwd
      </button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent className="rounded-none border-2 border-zinc-900 shadow-brutal">
          <AlertDialogHeader>
            <div className="label-overline text-purple-600 mb-1">Reset Client Password</div>
            <AlertDialogTitle className="font-display text-2xl font-black tracking-tighter uppercase">
              Reset password for {project.name}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-600">
              The client&apos;s password will be reset to <strong>{project.name}@123</strong>.
              You&apos;ll need to share the new password with them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-reset-password"
              className="rounded-none border-zinc-400 font-mono uppercase tracking-widest text-xs hover:bg-zinc-100">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction data-testid={`confirm-reset-password-${project.name}`} disabled={busy} onClick={doReset}
              className="rounded-none bg-purple-600 hover:bg-purple-700 text-white font-mono uppercase tracking-widest text-xs">
              {busy ? "Resetting…" : "Reset"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function ExportPdfButton({ project }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const { data } = await http.get(
        `/projects/${encodeURIComponent(project.name)}/pdf`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Project-${project.name}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <button
      data-testid={`export-pdf-${project.name}`}
      onClick={download}
      disabled={busy}
      title="Export project brief as PDF"
      className="label-overline text-zinc-600 hover:text-blue-700 px-2 py-1 flex items-center gap-1 disabled:opacity-50"
    >
      <FilePdf size={11} weight="bold" /> {busy ? "Exporting…" : "PDF"}
    </button>
  );
}


function Row({ icon: Icon, label, value, multiline }) {
  return (
    <div className="flex items-start gap-3">
      <Icon size={13} weight="bold" className="text-zinc-400 mt-1 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="label-overline text-zinc-500">{label}</div>
        <div className={`text-sm text-zinc-800 ${multiline ? "whitespace-pre-wrap" : "truncate"}`}>{value}</div>
      </div>
    </div>
  );
}

function SummaryLine({ k, v, sub, accent = "text-zinc-800", bold }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="label-overline text-zinc-500 shrink-0">{k}</span>
      <span className={`${accent} ${bold ? "font-bold" : ""} truncate text-right`}>
        {v}
        {sub && <span className="text-[10px] text-zinc-500 ml-1 normal-case tracking-normal">({sub})</span>}
      </span>
    </div>
  );
}

function ProjectDialog({ open, onOpenChange, editing, managers, onSaved }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setForm({
        name: editing.name,
        title: editing.title || "",
        category: editing.category || "civil",
        client_name: editing.client_name || "",
        client_contact: editing.client_contact || "",
        client_address: editing.client_address || "",
        scope: editing.scope || "",
        fee_type: editing.fee_type || "lumpsum",
        lumpsum_amount: editing.lumpsum_amount ?? "",
        rate_per_sqft: editing.rate_per_sqft ?? "",
        area_sqft: editing.area_sqft ?? "",
        start_date: editing.start_date || "",
        end_date: editing.end_date || "",
        status: editing.status || "active",
        manager_id: editing.manager_id || "__none__",
      });
    } else {
      setForm(emptyForm);
    }
  }, [editing, open]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const totalFee = (() => {
    if (form.fee_type === "lumpsum") return Number(form.lumpsum_amount) || 0;
    if (form.fee_type === "sqft") return (Number(form.rate_per_sqft) || 0) * (Number(form.area_sqft) || 0);
    return 0;
  })();

  const submit = async (e) => {
    e.preventDefault();
    if (!editing && !form.name.trim()) { toast.error("Job No. required"); return; }
    if (!form.client_name.trim() || !form.client_contact.trim() || !form.client_address.trim()) {
      toast.error("Client Name, Contact and Address are required"); return;
    }
    setSaving(true);
    try {
      const body = {
        ...form,
        lumpsum_amount: form.fee_type === "lumpsum" && form.lumpsum_amount !== "" ? Number(form.lumpsum_amount) : null,
        rate_per_sqft: form.fee_type === "sqft" && form.rate_per_sqft !== "" ? Number(form.rate_per_sqft) : null,
        area_sqft: form.fee_type === "sqft" && form.area_sqft !== "" ? Number(form.area_sqft) : null,
        manager_id: form.manager_id === "__none__" ? "" : form.manager_id,
      };
      if (editing) {
        const { name, ...patch } = body;
        await http.patch(`/projects/${encodeURIComponent(editing.name)}`, patch);
        toast.success("Project updated");
      } else {
        await http.post("/projects", body);
        toast.success(`Project ${body.name} created`);
      }
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-2 border-zinc-900 shadow-brutal max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="label-overline text-blue-600 mb-1">
            {editing ? "Modify Project" : "Create Project"}
          </div>
          <DialogTitle className="font-display text-2xl font-black tracking-tighter uppercase">
            {editing ? `Edit ${editing.name}` : "New Job"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2" data-testid="project-form">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Job No. *">
              <Input
                data-testid="project-name-input"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                disabled={!!editing}
                placeholder="e.g. 2026208 or DHP-047"
                className="rounded-none border-zinc-400 focus-visible:ring-blue-600 focus-visible:ring-offset-0"
                required
              />
            </Field>
            <Field label="Project Name / Title">
              <Input data-testid="project-title-input" value={form.title}
                     onChange={(e) => update("title", e.target.value)}
                     placeholder="e.g. Villa Verona"
                     className="rounded-none border-zinc-400 focus-visible:ring-blue-600 focus-visible:ring-offset-0" />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Category *">
              <Select value={form.category} onValueChange={(v) => update("category", v)}>
                <SelectTrigger data-testid="project-category-select"
                               className="rounded-none border-zinc-400 focus:ring-blue-600 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="civil" className="rounded-none" data-testid="project-cat-civil">Civil Work</SelectItem>
                  <SelectItem value="interior" className="rounded-none" data-testid="project-cat-interior">Interior Project</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Client Name *">
              <Input data-testid="project-client-name-input" value={form.client_name}
                     onChange={(e) => update("client_name", e.target.value)}
                     className="rounded-none border-zinc-400 focus-visible:ring-blue-600 focus-visible:ring-offset-0" required />
            </Field>
            <Field label="Contact No. *">
              <Input data-testid="project-client-contact-input" value={form.client_contact}
                     onChange={(e) => update("client_contact", e.target.value)}
                     placeholder="+91 …"
                     className="rounded-none border-zinc-400 focus-visible:ring-blue-600 focus-visible:ring-offset-0" required />
            </Field>
          </div>

          <Field label="Address *">
            <Textarea data-testid="project-client-address-input" value={form.client_address}
                      onChange={(e) => update("client_address", e.target.value)}
                      rows={2}
                      className="rounded-none border-zinc-400 focus-visible:ring-blue-600 focus-visible:ring-offset-0" required />
          </Field>

          <Field label="Scope of Work">
            <Textarea data-testid="project-scope-input" value={form.scope}
                      onChange={(e) => update("scope", e.target.value)}
                      rows={3}
                      placeholder="Design + working drawings + interior specs + site supervision …"
                      className="rounded-none border-zinc-400 focus-visible:ring-blue-600 focus-visible:ring-offset-0" />
          </Field>

          <Field label="Project Manager">
            <Select value={form.manager_id} onValueChange={(v) => update("manager_id", v)}>
              <SelectTrigger data-testid="project-manager-select"
                             className="rounded-none border-zinc-400 focus:ring-blue-600 focus:ring-offset-0">
                <SelectValue placeholder="Assign a manager (optional)" />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="__none__" className="rounded-none">— No manager assigned —</SelectItem>
                {managers.map((m) => (
                  <SelectItem key={m.id} value={m.id} className="rounded-none">
                    {m.name} · {m.role === "admin" ? "Admin" : "Manager"} · {m.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <div className="fee-panel-light border-2 p-4 space-y-3 rounded-none">
            <div className="fee-heading label-overline font-bold">Total Fees</div>
            <Field label="Fee Model">
              <Select value={form.fee_type} onValueChange={(v) => update("fee_type", v)}>
                <SelectTrigger data-testid="project-fee-type-select"
                               className="rounded-none border-zinc-400 focus:ring-orange-600 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="lumpsum" className="rounded-none">Lumpsum (₹ total)</SelectItem>
                  <SelectItem value="sqft" className="rounded-none">Per Sq.Ft × Area</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {form.fee_type === "lumpsum" ? (
              <Field label="Lumpsum Amount (₹)">
                <Input data-testid="project-lumpsum-input" type="number" min="0" step="1"
                       value={form.lumpsum_amount}
                       onChange={(e) => update("lumpsum_amount", e.target.value)}
                       className="rounded-none border-zinc-400 focus-visible:ring-orange-600 focus-visible:ring-offset-0" />
              </Field>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Rate per Sq.Ft (₹)">
                  <Input data-testid="project-rate-input" type="number" min="0" step="1"
                         value={form.rate_per_sqft}
                         onChange={(e) => update("rate_per_sqft", e.target.value)}
                         className="rounded-none border-zinc-400 focus-visible:ring-orange-600 focus-visible:ring-offset-0" />
                </Field>
                <Field label="Area (Sq.Ft)">
                  <Input data-testid="project-area-input" type="number" min="0" step="1"
                         value={form.area_sqft}
                         onChange={(e) => update("area_sqft", e.target.value)}
                         className="rounded-none border-zinc-400 focus-visible:ring-orange-600 focus-visible:ring-offset-0" />
                </Field>
              </div>
            )}
            <div data-testid="project-total-fee-preview"
                 className="fee-heading text-sm font-mono font-bold border-t border-orange-300 pt-2">
              Total: <strong>{money(totalFee)}</strong>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Start Date">
              <Input data-testid="project-start-input" type="date" value={form.start_date}
                     onChange={(e) => update("start_date", e.target.value)}
                     className="rounded-none border-zinc-400 focus-visible:ring-blue-600 focus-visible:ring-offset-0" />
            </Field>
            <Field label="Expected End">
              <Input data-testid="project-end-input" type="date" value={form.end_date}
                     onChange={(e) => update("end_date", e.target.value)}
                     className="rounded-none border-zinc-400 focus-visible:ring-blue-600 focus-visible:ring-offset-0" />
            </Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => update("status", v)}>
                <SelectTrigger data-testid="project-status-select"
                               className="rounded-none border-zinc-400 focus:ring-blue-600 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="active" className="rounded-none">Active</SelectItem>
                  <SelectItem value="on_hold" className="rounded-none">On Hold</SelectItem>
                  <SelectItem value="completed" className="rounded-none">Completed</SelectItem>
                  <SelectItem value="cancelled" className="rounded-none">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="flex items-center justify-end gap-3 pt-3 border-t border-zinc-300">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
                    className="rounded-none border-zinc-400 font-mono uppercase tracking-widest text-xs hover:bg-zinc-100">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} data-testid="project-submit-button"
                    className="rounded-none bg-zinc-900 hover:bg-blue-600 text-white font-mono uppercase tracking-widest text-xs">
              {saving ? "Saving…" : (editing ? "Save Changes" : "Create Project")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AdvancesDialog({ project, onOpenChange, onChanged }) {
  const open = !!project;
  const [items, setItems] = useState([]);
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = useCallback(async () => {
    if (!project) return;
    try {
      const { data } = await http.get(`/projects/${encodeURIComponent(project.name)}/advances`);
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [project]);

  useEffect(() => {
    if (open) {
      setAmount(""); setDate(""); setNote("");
      load();
    }
  }, [open, load]);

  const add = async (e) => {
    e.preventDefault();
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error("Amount must be greater than 0"); return; }
    setSaving(true);
    try {
      await http.post(`/projects/${encodeURIComponent(project.name)}/advances`, {
        amount: amt, date: date || null, note,
      });
      toast.success("Advance added");
      setAmount(""); setNote(""); setDate("");
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await http.delete(`/projects/${encodeURIComponent(project.name)}/advances/${pendingDelete.id}`);
      toast.success("Advance removed");
      setPendingDelete(null);
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const total = items.reduce((s, a) => s + (a.amount || 0), 0);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="rounded-none border-2 border-zinc-900 shadow-brutal max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="label-overline text-emerald-700 mb-1">Advance Deposits · Ledger</div>
            <DialogTitle className="font-display text-2xl font-black tracking-tighter uppercase">
              {project?.name} · Advances
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-end border border-emerald-400 bg-emerald-50/60 p-3 mt-2"
                data-testid="advance-add-form">
            <Field label="Amount (₹) *">
              <Input data-testid="advance-amount-input" type="number" min="1" step="1"
                     value={amount} onChange={(e) => setAmount(e.target.value)}
                     className="rounded-none border-zinc-400 focus-visible:ring-emerald-600 focus-visible:ring-offset-0" required />
            </Field>
            <Field label="Date">
              <Input data-testid="advance-date-input" type="date"
                     value={date} onChange={(e) => setDate(e.target.value)}
                     className="rounded-none border-zinc-400 focus-visible:ring-emerald-600 focus-visible:ring-offset-0" />
            </Field>
            <Field label="Note">
              <Input data-testid="advance-note-input" value={note}
                     onChange={(e) => setNote(e.target.value)}
                     placeholder="Cheque no · UPI ref · etc."
                     className="rounded-none border-zinc-400 focus-visible:ring-emerald-600 focus-visible:ring-offset-0" />
            </Field>
            <Button type="submit" disabled={saving} data-testid="advance-submit-button"
                    className="rounded-none bg-emerald-700 hover:bg-emerald-800 text-white font-mono uppercase tracking-widest text-xs h-10">
              {saving ? "Adding…" : "Add"}
            </Button>
          </form>

          <div className="mt-4 border border-zinc-300 overflow-x-auto" data-testid="advance-list">
            <div className="min-w-[560px]">
            <div className="grid grid-cols-[110px_120px_1fr_140px_40px] gap-0 bg-zinc-100 border-b border-zinc-300">
              <HeadCell>Date</HeadCell>
              <HeadCell>Amount</HeadCell>
              <HeadCell>Note</HeadCell>
              <HeadCell>Received By</HeadCell>
              <HeadCell />
            </div>
            {items.length === 0 && (
              <div className="p-6 text-center label-overline text-zinc-400">No advances recorded yet</div>
            )}
            {items.map((a) => (
              <div key={a.id} data-testid={`advance-row-${a.id}`}
                   className="grid grid-cols-[110px_120px_1fr_140px_40px] gap-0 border-b border-zinc-200 last:border-b-0">
                <Cell className="font-mono text-xs">{a.date || "—"}</Cell>
                <Cell className="font-mono text-sm font-bold text-emerald-800">{money(a.amount)}</Cell>
                <Cell className="text-sm text-zinc-700 truncate">{a.note || "—"}</Cell>
                <Cell className="text-xs text-zinc-500">{a.received_by || "—"}</Cell>
                <Cell>
                  <button
                    data-testid={`delete-advance-${a.id}`}
                    onClick={() => setPendingDelete(a)}
                    className="text-zinc-400 hover:text-red-600"
                    aria-label="Delete advance"
                  >
                    <Trash size={13} weight="bold" />
                  </button>
                </Cell>
              </div>
            ))}
            {items.length > 0 && (
              <div className="grid grid-cols-[110px_120px_1fr_140px_40px] gap-0 border-t-2 border-zinc-900 bg-zinc-50">
                <Cell className="label-overline">Total</Cell>
                <Cell className="font-mono font-bold text-emerald-800" data-testid="advance-total">{money(total)}</Cell>
                <Cell />
                <Cell />
                <Cell />
              </div>
            )}
            </div>
          </div>

          <div className="flex justify-end pt-3 border-t border-zinc-300 mt-3">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
                    className="rounded-none border-zinc-400 font-mono uppercase tracking-widest text-xs hover:bg-zinc-100">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!pendingDelete} onOpenChange={(v) => !v && setPendingDelete(null)}>
        <AlertDialogContent className="rounded-none border-2 border-zinc-900 shadow-brutal">
          <AlertDialogHeader>
            <div className="label-overline text-red-600 mb-1">Delete Advance</div>
            <AlertDialogTitle className="font-display text-2xl font-black tracking-tighter uppercase">
              Remove this advance?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-600">
              This will permanently delete the entry of <strong>{money(pendingDelete?.amount || 0)}</strong> on <strong>{pendingDelete?.date || "—"}</strong>. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-advance"
              className="rounded-none border-zinc-400 font-mono uppercase tracking-widest text-xs hover:bg-zinc-100">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-advance" onClick={confirmDelete}
              className="rounded-none bg-red-600 hover:bg-red-700 text-white font-mono uppercase tracking-widest text-xs">
              Yes, Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function SendLoginDialog({ project, onOpenChange }) {
  const open = !!project;
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) { setEmail(""); setResult(null); }
  }, [open]);

  const send = async (e) => {
    e.preventDefault();
    const em = email.trim();
    if (!em || !em.includes("@")) { toast.error("Enter a valid email address"); return; }
    setSending(true);
    setResult(null);
    try {
      const { data } = await http.post(
        `/projects/${encodeURIComponent(project.name)}/send-login-email`,
        { email: em }
      );
      setResult(data);
      if (data.sent) {
        toast.success(`Login details emailed to ${em}`);
      } else {
        toast.info(data.reason || "Email service not configured — details shown below.");
      }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-2 border-zinc-900 shadow-brutal max-w-lg">
        <DialogHeader>
          <div className="label-overline text-purple-700 mb-1">Client Onboarding</div>
          <DialogTitle className="font-display text-2xl font-black tracking-tighter uppercase">
            Send Portal Login
          </DialogTitle>
        </DialogHeader>
        <div className="text-sm text-zinc-600 mb-3">
          Send the client-portal URL, username, and password to your client via email.
        </div>
        <form onSubmit={send} className="space-y-3" data-testid="send-login-form">
          <Field label="Client Email *">
            <Input
              data-testid="send-login-email-input"
              type="email" required autoFocus
              placeholder="client@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-none border-zinc-400 focus-visible:ring-purple-600 focus-visible:ring-offset-0"
            />
          </Field>
          <div className="flex items-center justify-end gap-3 pt-3 border-t border-zinc-300">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
                    className="rounded-none border-zinc-400 font-mono uppercase tracking-widest text-xs hover:bg-zinc-100">
              Cancel
            </Button>
            <Button type="submit" disabled={sending} data-testid="send-login-submit"
                    className="rounded-none bg-purple-700 hover:bg-purple-800 text-white font-mono uppercase tracking-widest text-xs">
              <EnvelopeSimple size={13} weight="bold" className="mr-2" />
              {sending ? "Sending…" : "Send Email"}
            </Button>
          </div>
        </form>
        {result && !result.sent && (
          <div className="mt-4 border border-amber-400 bg-amber-50 p-3" data-testid="send-login-fallback">
            <div className="label-overline text-amber-800 mb-2">Email not configured</div>
            <div className="text-xs text-zinc-700 mb-3 leading-relaxed">{result.reason}</div>
            <div className="grid grid-cols-1 gap-2 text-xs font-mono text-zinc-800">
              <div><strong>Portal URL:</strong> <a href={result.login_url} target="_blank" rel="noreferrer" className="underline">{result.login_url}</a></div>
              <div><strong>Username:</strong> {result.username}</div>
              <div><strong>Password:</strong> {result.password}</div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label-overline block mb-1.5 text-zinc-700">{label}</label>
      {children}
    </div>
  );
}

function HeadCell({ children, className = "" }) {
  return <div className={`label-overline text-zinc-600 px-3 py-2 flex items-center ${className}`}>{children}</div>;
}
function Cell({ children, className = "" }) {
  return <div className={`px-3 py-2 flex items-center min-w-0 ${className}`}>{children}</div>;
}
